import React, { useEffect, useMemo, useRef, useState } from "react";
import { demoMatchProgress } from "../../shared/progress";
import { wormColors } from "core/src/core/constants.js";
import { flagUrlSquare } from "../../shared/flags";
import { modeLook, ROUND_MODE_NAMES } from "../../online/dynamic";
import type { DynamicKind, NextRound, RoundResult, RoundSetup } from "../../online/dynamic";
import type { DynamicLayerProps, PublicPlayer } from "../play/dynamic/types";
import PlayNowLayer from "../play/dynamic/PlayNowLayer";
import RoyaleLayer from "../play/dynamic/RoyaleLayer";
import NextRoundReveal, { isBoard, isStamp, REVEAL_VARIANTS, standingsFor, revealLeadMs, revealMs, revealStraddles, revealVariant } from "../play/dynamic/NextRoundReveal";
import type { RevealVariant } from "../play/dynamic/NextRoundReveal";

// /debug/playnow-layer: the Play Now layer (and the next round's reveal) over a
// fake board, with mock data and a stepper to jump between every state. The
// time-driven ones replay by themselves. Query: ?s=<state>&mode=<0-5>&ctx=royale&ui=0
// (ui=0 hides the lab's controls, for screenshots; &n=5|7|8|12 players and &me=low
// for the standings in the banner). The reveal's designs:
// &v=<slam|banner|chip|corner|stamp|edge|pass> picks one for the Reveal step,
// &view=compare shows them all at once in PC and landscape phone frames
// (&only=stamps: just the stamps; &only=boards: the banner and the five
// standings boards through a whole pause, with &n= and &me=low). The
// other steps go through the real layer, which plays the design picked with
// ?reveal=<name> (as a real game does).

const PLAYERS: PublicPlayer[] = [
  { id: "p1", name: "TurboNoodle", colorId: "pink", flag: "se" },
  { id: "p2", name: "SilentSnek", colorId: "lime", flag: "de" },
  { id: "p3", name: "Kurvenkönig", colorId: "blue", flag: "pl" },
  { id: "p4", name: "WigglyWill", colorId: "purple", flag: "no" },
  { id: "p5", name: "TrailMix", colorId: "orange", flag: "nl" },
  { id: "p6", name: "Zigzagga", colorId: "indigo", flag: "fi" },
  { id: "p7", name: "LoopDeLoop", colorId: "teal", flag: "dk" },
  { id: "me", name: "Mathias", colorId: "green", flag: "gb" },
];
// A fuller room, for the standings (&n=12): the same eight and four more.
const MORE: PublicPlayer[] = [
  { id: "p8", name: "Slingerslang", colorId: "yellow", flag: "be" },
  { id: "p9", name: "Curvy", colorId: "red", flag: "fr" },
  { id: "p10", name: "NoBrakesNora", colorId: "cyan", flag: "es" },
  { id: "p11", name: "Hal", colorId: "pink", flag: "it" },
];
const MORE_SCORE: Record<string, number> = { p8: 17, p9: 12, p10: 10, p11: 4 };
const BY_ID: Record<string, PublicPlayer> = Object.fromEntries(PLAYERS.concat(MORE).map((p) => [p.id, p]));
const SET_SCORE: Record<string, number> = { p4: 41, p3: 36, p1: 33, me: 30, p6: 23, p2: 18, p5: 15, p7: 11 };
const MID_SCORE: Record<string, number> = { p4: 19, me: 15, p3: 14, p1: 13, p6: 9, p2: 7, p5: 5, p7: 2 };

type StateId = "drop" | "over" | "royale" | "bigger" | "smaller" | "reveal" | "setover" | "moved";
const STATES: { id: StateId; label: string }[] = [
  { id: "drop", label: "Dropping in" },
  { id: "over", label: "Round over" },
  { id: "royale", label: "Royale round" },
  { id: "bigger", label: "Bigger map" },
  { id: "smaller", label: "Smaller map" },
  { id: "reveal", label: "Reveal" },
  { id: "setover", label: "Set over" },
  { id: "moved", label: "Room moved" },
];
const ROUND_PAUSE = 6500, SET_PAUSE = 12000;
const SET_ROUNDS = 2; // as the server has it for now

const q = () => new URLSearchParams(window.location.search);
const colorOf = (p: PublicPlayer) => (wormColors as Record<string, string>)[p.colorId];

// ---------- the fake game: a round in progress, as the reveal finds it ----------
// Drawn into its parent's box, so the same one fills the screen or a small frame.
// [trail, head x, head y] on a 1000 x 500 board; a crashed worm has no head.
const TRAILS: [string, number, number, boolean][] = [
  ["M120 380c80-140 190-60 240-170s150-90 210-20c40 50 10 120-60 130s-120-40-90-100", 630, 150, true],
  ["M840 110c-120 30-90 150-220 150s-130 120-260 110c-90-10-150-70-120-150", 240, 220, true],
  ["M200 110c110 10 130 110 250 90s170 60 150 150c-10 60-80 90-140 60", 460, 410, false],
  ["M760 400c-40-110 60-150 30-240s-110-90-190-60", 600, 100, true],
  ["M420 70c60 40 170 10 250 50s120 110 60 180", 730, 300, false],
  ["M90 230c70 30 60 90 140 100s130 80 220 60", 450, 390, false],
  ["M880 330c-60 60-150 40-180-30s40-140 120-130", 820, 170, true],
  ["M300 440c90 20 160-30 250-10s150 20 230-40", 780, 390, true],
  // a busy middle: the reveal's words sit over these
  ["M180 250c120-40 220 40 330 0s200-50 320 10", 830, 260, true],
  ["M250 300c100 30 180-60 280-30s190 50 260-20", 790, 250, false],
  ["M330 200c80 50 200-20 300 30s120 40 190 10", 820, 240, false],
];
// Where the next round's worms wait: [x, y, heading in degrees].
const STARTS: [number, number, number][] = [[160, 150, 20], [840, 140, 160], [500, 90, 80], [260, 380, -40], [740, 380, -140], [500, 420, -90], [110, 270, 0], [890, 270, 180]];
const POWERUPS: [number, number, string][] = [[340, 120, "#3ddc84"], [690, 420, "#ff5c6c"], [540, 260, "#4fa8ff"], [150, 420, "#3ddc84"]];

// The game's own 3, 2, 1 as the next round starts.
function Count() {
  const [n, setN] = useState(3);
  useEffect(() => { const id = window.setInterval(() => setN((v) => v - 1), 1000); return () => window.clearInterval(id); }, []);
  return n > 0 ? <b style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", font: '700 120px/1 "Chakra Petch", sans-serif', color: "#e9f3ff", textShadow: "0 0 30px rgba(40,196,212,0.7)" }}>{n}</b> : null;
}

interface FakeGameProps {
  phone: boolean;
  fresh: boolean;                 // the next round's board: worms at their starts, no trails yet
  players: PublicPlayer[];
  score: Record<string, number>;
  setup: RoundSetup;
}
function FakeGame({ phone, fresh, players, score, setup }: FakeGameProps) {
  const look = modeLook(setup.mode);
  const side = phone ? "max(44px, env(safe-area-inset-left))" : "max(20px, env(safe-area-inset-left))";
  const sideR = phone ? "max(44px, env(safe-area-inset-right))" : "max(20px, env(safe-area-inset-right))";
  const hud: React.CSSProperties = { font: '600 11px/1 "Chakra Petch", sans-serif', letterSpacing: 1, textTransform: "uppercase", color: "#8ea3bd", display: "flex", alignItems: "baseline", gap: 6 };
  const big: React.CSSProperties = { font: '700 20px/1 "Chakra Petch", sans-serif', color: "#e9f3ff", letterSpacing: 0 };
  return (
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(120% 80% at 60% 0%, #0d1830 0%, #060b18 60%)", color: "#e9f3ff", fontFamily: '"Outfit", system-ui, sans-serif', overflow: "hidden" }}>
      <div style={{ position: "absolute", top: phone ? 50 : 76, left: side, right: sideR, bottom: phone ? 52 : 82, borderRadius: 999, border: `${phone ? 5 : 9}px solid #2b3a57`, background: "#0c1220", overflow: "hidden" }}>
        <svg viewBox="0 0 1000 500" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} fill="none" strokeLinecap="round" aria-hidden="true">
          {!fresh && TRAILS.map(([d], i) => <path key={i} d={d} stroke={colorOf(PLAYERS[i % PLAYERS.length])} strokeWidth={6} vectorEffect="non-scaling-stroke" />)}
          {!fresh && POWERUPS.map(([x, y, c], i) => <circle key={i} cx={x} cy={y} r={13} fill={c} fillOpacity={0.25} stroke={c} strokeWidth={3} />)}
          {!fresh && TRAILS.map(([, x, y, alive], i) => alive && <circle key={i} cx={x} cy={y} r={7} fill={colorOf(PLAYERS[i % PLAYERS.length])} />)}
          {fresh && STARTS.map(([x, y, a], i) => (
            <g key={i} transform={`translate(${x} ${y}) rotate(${a})`} stroke={colorOf(PLAYERS[i])} fill={colorOf(PLAYERS[i])}>
              <circle r={8} stroke="none" /><path d="M16 0h26M34 -7l9 7-9 7" strokeWidth={4} fill="none" strokeLinejoin="round" />
            </g>
          ))}
        </svg>
        {fresh && <Count />}
      </div>
      <div style={{ position: "absolute", top: phone ? 6 : 14, left: side, height: 36, display: "flex", alignItems: "center", gap: 16 }}>
        <span style={hud}>Round <b style={big}>{setup.royaleRound || setup.setRound}</b>{setup.royaleRound ? "" : `/${setup.setRounds}`}</span>
        <span style={hud}>Alive <b style={{ ...big, color: "#28c4d4" }}>{fresh ? 8 : 5}</b>/8</span>
        <span style={{ ...hud, padding: "6px 10px", borderRadius: 6, color: look.color, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", textTransform: "none", letterSpacing: 0.4, fontSize: 12 }}>{look.name}</span>
      </div>
      <div style={{ position: "absolute", top: phone ? 6 : 14, right: sideR, display: "flex", gap: 8 }}>
        {[0, 1, 2, 3].map((i) => <i key={i} style={{ width: phone ? 38 : 32, height: phone ? 38 : 32, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.14)", display: "grid", placeItems: "center" }}><u style={{ width: 12, height: 12, borderRadius: 3, border: "1.5px solid #8ea3bd" }} /></i>)}
      </div>
      <div style={{ position: "absolute", bottom: phone ? 6 : 16, left: side, right: sideR, height: phone ? 38 : 46, display: "flex", gap: 6 }}>
        {players.map((p) => (
          <span key={p.id} style={{ flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center", gap: 6, padding: "0 8px", borderRadius: 8, background: "rgba(255,255,255,0.05)", borderBottom: `3px solid ${colorOf(p)}`, fontSize: 12 }}>
            <img src={flagUrlSquare(p.flag as string)} alt="" style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover", flex: "none" }} />
            <b style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{p.name}</b>
            <em style={{ fontStyle: "normal", fontWeight: 700 }}>{score[p.id] || 0}</em>
          </span>
        ))}
      </div>
    </div>
  );
}

// The frames of the compare view: a PC screen and a landscape phone.
const FRAMES = [{ id: "pc", label: "PC", w: 1440, h: 900, cols: 2 }, { id: "phone", label: "Phone", w: 844, h: 390, cols: 3 }];
const PHONE_Q = "(max-height: 500px) and (orientation: landscape)";

export default function PlayNowLayerLab() {
  React.useEffect(() => { demoMatchProgress(); }, []);
  const [state, setState] = useState<StateId>(() => (q().get("view") === "compare" ? "reveal" : STATES.find((x) => x.id === q().get("s"))?.id || "drop"));
  const [modeIx, setModeIx] = useState(() => Math.max(0, Math.min(5, Number(q().get("mode")) || 0)));
  const [ctx, setCtx] = useState<DynamicKind>(() => (q().get("ctx") === "royale" ? "royale" : "playnow"));
  const [ui] = useState(() => q().get("ui") !== "0");
  const [count, setCount] = useState(() => ([5, 7, 8, 12].indexOf(Number(q().get("n"))) !== -1 ? Number(q().get("n")) : 8));
  const [meLow, setMeLow] = useState(() => q().get("me") === "low");
  const [variant, setVariant] = useState<RevealVariant>(() => (REVEAL_VARIANTS.find((x) => x === q().get("v")) || revealVariant()));
  const [compare, setCompare] = useState(() => q().get("view") === "compare");
  const [stampsOnly, setStampsOnly] = useState(() => q().get("only") === "stamps");
  const [boardsOnly, setBoardsOnly] = useState(() => q().get("only") === "boards");
  // The reveal step plays a whole pause (the standings, then the next round) for
  // the boards, and for the banner beside them.
  const wholePause = (v: RevealVariant) => isBoard(v) || (v === "banner" && compare && boardsOnly);
  const BEAT = ROUND_PAUSE - revealLeadMs("banner");
  const [since, setSince] = useState(0);         // ms into the reveal step's loop, at the moments a board changes
  const [phone, setPhone] = useState(() => window.matchMedia(PHONE_Q).matches);
  const [winW, setWinW] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => { setPhone(window.matchMedia(PHONE_Q).matches); setWinW(window.innerWidth); };
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const [run, setRun] = useState(0);            // bumps to replay
  const [pauseAt, setPauseAt] = useState(() => Date.now());
  const [playing, setPlaying] = useState(false); // the short "next round" gap before a pause replays
  const startRef = useRef(Date.now());

  const mode = ROUND_MODE_NAMES[modeIx];
  const timed = state === "over" || state === "royale" || state === "bigger" || state === "smaller" || state === "setover";
  const pauseMs = state === "setover" ? SET_PAUSE : ROUND_PAUSE;

  const go = (id: StateId) => { setState(id); setRun((r) => r + 1); };
  // Every (re)play starts its pause now; a finished pause plays the next round
  // for a moment and then starts over.
  useEffect(() => {
    startRef.current = Date.now();
    (window as any).__labRun = run; // for screenshot scripts: a new loop has begun
    setPauseAt(Date.now());
    setPlaying(false);
    setSince(0);
    if (timed) {
      const a = window.setTimeout(() => setPlaying(true), pauseMs);
      const b = window.setTimeout(() => setRun((r) => r + 1), pauseMs + 1600);
      return () => { window.clearTimeout(a); window.clearTimeout(b); };
    }
    if (state === "reveal") {
      // The round change of the designs that stay on into the next round: their
      // board becomes the next round's while they are up.
      const whole = compare ? boardsOnly : isBoard(variant);
      const leads = whole ? [ROUND_PAUSE] : REVEAL_VARIANTS.filter(revealStraddles).map(revealLeadMs);
      const a = leads.map((ms) => window.setTimeout(() => setSince((cur) => Math.max(cur, ms)), ms));
      // The loop: the design's own length (the longest one's when they all play) and a breath.
      const len = whole ? BEAT + revealMs("banner") : compare && !stampsOnly ? Math.max(...REVEAL_VARIANTS.map(revealMs)) : revealMs(compare ? "stamp" : variant);
      const b = window.setTimeout(() => setRun((r) => r + 1), Math.max(4300, len + 1100));
      return () => { a.forEach((id) => window.clearTimeout(id)); window.clearTimeout(b); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, state]);

  const setup: RoundSetup = useMemo(() => ({
    dynamic: "playnow", mode: "Fatboy Slim", mapDesc: null,
    sizeKey: state === "bigger" ? "medium" : state === "smaller" ? "large" : "medium", shape: "Stadium",
    round: 10, scoreReset: false, set: 2,
    setRound: state === "setover" ? SET_ROUNDS : 1, setRounds: SET_ROUNDS,
    // the Royale step: round 3 of Royale #214, 27 in it, 3 go out
    ...(state === "royale" ? { dynamic: "royale" as const, number: 214, stage: "large" as const, alive: 27, out: 3, final: false, finalWins: 3, size: 30, royaleRound: 3 } : {}),
  }), [state, run]);
  const next: NextRound | null = !timed ? null : state === "setover"
    ? { dynamic: "playnow", mode, shape: "Rectangle", sizeKey: "medium", set: 3, setRound: 1, setRounds: SET_ROUNDS, scoreReset: true, setOver: true }
    : state === "royale" ? { dynamic: "royale", mode, shape: "Stadium", sizeKey: "large", number: 214, royaleRound: 4, alive: 24, out: 3, final: false, finalWins: 3 }
    : { dynamic: "playnow", mode, shape: "Stadium", sizeKey: state === "bigger" ? "large" : "medium", set: 2, setRound: 2, setRounds: SET_ROUNDS, sizeChange: state === "bigger" ? "bigger" : state === "smaller" ? "smaller" : null };
  const result: RoundResult | null = !timed ? null : state === "setover"
    ? { setOver: true, standings: Object.entries(SET_SCORE).map(([id, score]) => ({ id, score })) }
    : state === "royale" ? { royale: true, out: ["p7", "p5", "p2"], places: { p7: 27, p5: 26, p2: 25 }, alive: 24 }
    : {};

  // The fake round someone watches: a player crashes every few seconds.
  const getGameState = () => {
    const out = Math.min(6, Math.floor((Date.now() - startRef.current) / 2500) % 8);
    return { players: PLAYERS.slice(0, 7).map((p, i) => ({ id: p.id, alive: i >= out })), gameEvents: [] };
  };

  // The room for the standings: 5, 8 or 12 players, me near the top or far down.
  const roster = count === 12 ? PLAYERS.concat(MORE) : PLAYERS.slice(8 - count);
  const midScore = { ...MID_SCORE, ...MORE_SCORE, me: meLow ? 3 : 15 };
  const phase: DynamicLayerProps["phase"] = timed && !playing ? "countdown" : "playing";
  const watching = state === "drop";
  const layer: DynamicLayerProps = {
    kind: "playnow", phase, setup,
    next: phase === "countdown" ? next : null,
    result: phase === "countdown" ? result : null,
    royaleResult: null,
    players: watching ? PLAYERS.slice(0, 7) : roster, playerById: BY_ID,
    score: state === "setover" ? SET_SCORE : midScore,
    roundWinnerId: phase === "countdown" && state !== "setover" ? "p4" : null,
    roundWinnerPoints: phase === "countdown" && state !== "setover" ? 7 : null,
    seatIds: ["me"], youIds: watching ? [] : ["me"], pending: watching, myPlace: null,
    pauseStartedAt: pauseAt, pauseMs, moved: state === "moved", touch: false,
    getGameState, onLeave: () => go("drop"),
  };

  // One reveal over the fake game.
  const scene = (v: RevealVariant, isPhone: boolean) => { const whole = wholePause(v); const fresh = whole ? since >= ROUND_PAUSE : revealStraddles(v) && since >= revealLeadMs(v); return (
    <>
      <FakeGame phone={isPhone} fresh={fresh} players={whole ? roster : PLAYERS} score={whole ? midScore : MID_SCORE} setup={fresh ? { ...setup, mode } : setup} />
      <NextRoundReveal
        key={`${run}-${modeIx}-${ctx}-${v}-${whole}`}
        variant={v}
        mode={mode}
        shape={modeIx % 2 ? "Rectangle" : "Stadium"}
        sizeKey={ctx === "royale" ? "huge" : "large"}
        context={ctx}
        kicker={ctx === "royale" ? "Royale #214 · Round 3" : `Round 2/${SET_ROUNDS}`}
        line={ctx === "royale" ? "24 left · 3 go out this round" : undefined}
        durationMs={whole ? BEAT + revealMs(v) : revealMs(v)}
        barMs={whole ? ROUND_PAUSE : v === "banner" ? revealLeadMs(v) : undefined}
        beatMs={whole ? BEAT : undefined}
        standings={whole && ctx !== "royale" ? standingsFor(roster, midScore, ["me"], "p4", 7, "Round 1") : undefined}
      />
    </>
  ); };
  const room = Math.max(320, winW - (ui ? 172 : 32));

  return (
    <div style={{ position: "fixed", inset: 0, background: "#060b18", color: "#e9f3ff", fontFamily: '"Outfit", system-ui, sans-serif', overflow: "hidden" }}>
      {compare ? (
        // ---------- every design at once, in a PC and a phone frame ----------
        <div style={{ position: "absolute", inset: 0, overflowY: "auto", padding: `16px ${ui ? 156 : 16}px 40px 16px`, boxSizing: "border-box" }}>
          {FRAMES.map((f) => {
            const cols = winW < 900 ? Math.min(2, f.cols) : f.cols;
            const k = (room - (cols - 1) * 14) / cols / f.w;
            return (
              <section key={f.id} style={{ marginBottom: 28 }}>
                <h2 style={{ margin: "0 0 10px", font: '700 12px/1 "Chakra Petch", sans-serif', letterSpacing: 2, textTransform: "uppercase", color: "#8ea3bd" }}>{f.label} {f.w} x {f.h}</h2>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, ${f.w * k}px)`, gap: 14 }}>
                  {REVEAL_VARIANTS.filter((v) => (boardsOnly ? v === "banner" || isBoard(v) : !stampsOnly || isStamp(v))).map((v) => (
                    <figure key={v} style={{ margin: 0 }}>
                      <figcaption style={{ font: '700 12px/1 "Chakra Petch", sans-serif', letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6, cursor: "pointer" }} onClick={() => { setVariant(v); setCompare(false); go("reveal"); }}>{v}</figcaption>
                      <div style={{ width: f.w * k, height: f.h * k, overflow: "hidden", borderRadius: 6, border: "1px solid rgba(255,255,255,0.16)" }}>
                        {/* the transform makes this box the reveal's screen (it is position: fixed) */}
                        <div style={{ position: "relative", width: f.w, height: f.h, transform: `scale(${k})`, transformOrigin: "0 0", overflow: "hidden" }}>
                          {scene(v, f.id === "phone")}
                        </div>
                      </div>
                    </figure>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      ) : state === "reveal" ? scene(variant, phone) : (
        <>
          <FakeGame phone={phone} fresh={playing} players={layer.players} score={layer.score} setup={playing && next ? { ...setup, ...next } as RoundSetup : setup} />
          {/* ---------- what is being tested ---------- */}
          {state === "royale" ? <RoyaleLayer {...layer} kind="royale" /> : <PlayNowLayer {...layer} />}
        </>
      )}

      {/* ---------- lab controls ---------- */}
      {ui && (
        <nav aria-label="Lab: jump between states" style={{ position: "fixed", zIndex: 100, right: 8, top: "50%", transform: "translateY(-50%)", width: 132, maxHeight: "calc(100vh - 12px)", overflowY: "auto", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 3, padding: 6, borderRadius: 12, background: "rgba(0,0,0,0.72)", border: "1px dashed rgba(255,255,255,0.28)", font: "11px ui-monospace, Menlo, monospace" }}>
          {STATES.map((st) => (
            <button key={st.id} onClick={() => { setCompare(false); go(st.id); }} style={labBtn(!compare && state === st.id)}>{st.label}</button>
          ))}
          <span style={{ height: 1, background: "rgba(255,255,255,0.2)", margin: "3px 0" }} />
          <span style={{ color: "rgba(255,255,255,0.5)", padding: "0 4px" }}>next mode</span>
          {ROUND_MODE_NAMES.map((m, i) => (
            <button key={m} onClick={() => { setModeIx(i); setRun((r) => r + 1); }} style={{ ...labBtn(modeIx === i), color: modeIx === i ? "#03140f" : modeLook(m).color }}>{modeLook(m).name}</button>
          ))}
          <span style={{ height: 1, background: "rgba(255,255,255,0.2)", margin: "3px 0" }} />
          <div style={{ display: "flex", gap: 3 }}>
            {(["playnow", "royale"] as DynamicKind[]).map((c) => (
              <button key={c} onClick={() => { setCtx(c); setState("reveal"); setRun((r) => r + 1); }} style={{ ...labBtn(ctx === c), flex: 1, textAlign: "center" }}>{c === "playnow" ? "Play Now" : "Royale"}</button>
            ))}
          </div>
          <span style={{ height: 1, background: "rgba(255,255,255,0.2)", margin: "3px 0" }} />
          <span style={{ color: "rgba(255,255,255,0.5)", padding: "0 4px" }}>reveal</span>
          {REVEAL_VARIANTS.map((v) => (
            <button key={v} onClick={() => { setVariant(v); setCompare(false); go("reveal"); }} style={labBtn(!compare && state === "reveal" && variant === v)}>{v}</button>
          ))}
          <button onClick={() => { setCompare(true); setStampsOnly(false); setBoardsOnly(false); go("reveal"); }} style={labBtn(compare && !stampsOnly && !boardsOnly)}>compare all</button>
          <button onClick={() => { setCompare(true); setStampsOnly(false); setBoardsOnly(true); go("reveal"); }} style={labBtn(compare && boardsOnly)}>boards</button>
          <button onClick={() => { setCompare(true); setStampsOnly(true); setBoardsOnly(false); go("reveal"); }} style={labBtn(compare && stampsOnly && !boardsOnly)}>stamps only</button>
          <span style={{ height: 1, background: "rgba(255,255,255,0.2)", margin: "3px 0" }} />
          <div style={{ display: "flex", gap: 3 }}>
            {[5, 7, 8, 12].map((n) => <button key={n} onClick={() => { setCount(n); go(compare || state === "reveal" ? "reveal" : "over"); }} style={{ ...labBtn(count === n), flex: 1, textAlign: "center" }}>{n}</button>)}
            <button onClick={() => { setMeLow((v) => !v); go(compare || state === "reveal" ? "reveal" : "over"); }} style={{ ...labBtn(meLow), flex: 2, textAlign: "center" }}>me low</button>
          </div>
          <button onClick={() => setRun((r) => r + 1)} style={{ ...labBtn(false), textAlign: "center", border: "1px solid rgba(25,240,176,0.5)", color: "#19f0b0" }}>Replay</button>
        </nav>
      )}
    </div>
  );
}

const labBtn = (on: boolean): React.CSSProperties => ({
  border: 0, borderRadius: 6, padding: "4px 7px", textAlign: "left", cursor: "pointer", font: "inherit",
  background: on ? "#19f0b0" : "transparent", color: on ? "#03140f" : "rgba(255,255,255,0.75)", fontWeight: on ? 700 : 400,
});
